import { ModalConfirmation, notify, useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { ModalId } from '../../../../enums/modalId'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useDeleteContact } from '../../hooks/useDeleteContact'
import { SelectedContact } from '../../types/SelectedContact'

type DeleteContactConfirmationModalVariant = 'invoiceEditor' | 'contactsList' | 'supplierDetail'

const translationKeysByVariant = {
  invoiceEditor: {
    cancel: 'external_invoices.editor.form.field.receiver.delete.confirmation_modal.cancel',
    confirmDelete: 'external_invoices.editor.form.field.receiver.delete.confirmation_modal.confirm_delete',
    message: 'external_invoices.editor.form.field.receiver.delete.confirmation_modal.message',
    title: 'external_invoices.editor.form.field.receiver.delete.confirmation_modal.title',
    notificationSuccess: 'external_invoices.editor.form.field.receiver.delete.notification.success',
    notificationError: 'external_invoices.editor.form.field.receiver.delete.notification.error',
    notificationErrorUndeletable: 'external_invoices.editor.form.field.receiver.delete.notification.error.undeletable',
  },
  contactsList: {
    cancel: 'contacts.list.delete.confirmation_modal.cancel',
    confirmDelete: 'contacts.list.delete.confirmation_modal.confirm',
    message: 'contacts.list.delete.confirmation_modal.message',
    title: 'contacts.list.delete.confirmation_modal.title',
    notificationSuccess: 'contacts.list.delete.notification.success',
    notificationError: 'contacts.list.delete.notification.error',
    notificationErrorUndeletable: 'contacts.list.delete.notification.error.undeletable',
  },
  supplierDetail: {
    cancel: 'suppliers.detail.delete.confirmation_modal.cancel',
    confirmDelete: 'suppliers.detail.delete.confirmation_modal.confirm',
    message: 'suppliers.detail.delete.confirmation_modal.message',
    title: 'suppliers.detail.delete.confirmation_modal.title',
    notificationSuccess: 'suppliers.detail.delete.notification.success',
    notificationError: 'suppliers.detail.delete.notification.error',
    notificationErrorUndeletable: 'suppliers.detail.delete.notification.error.undeletable',
  },
} satisfies Record<DeleteContactConfirmationModalVariant, Record<string, string>>

export interface DeleteContactConfirmationModalProps {
  onClose?: () => void
  onDeleteContactSuccess?: (contactId: string) => void
  selectedContact: SelectedContact
  variant?: DeleteContactConfirmationModalVariant
}

export const DeleteContactConfirmationModal = ({
  onClose,
  onDeleteContactSuccess,
  selectedContact,
  variant = 'invoiceEditor',
}: DeleteContactConfirmationModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const translationKeys = translationKeysByVariant[variant]

  const { close } = useModal(ModalId.DeleteContactConfirmationModal, {
    onClose,
  })

  const { delete: deleteContact, isProcessing } = useDeleteContact({
    onSuccess: (contactId) => {
      queryClient.invalidateQueries([QueryKeys.Contacts])
      queryClient.invalidateQueries([QueryKeys.ContactsAndContactPersons])
      queryClient.invalidateQueries([QueryKeys.Contact, contactId])
      onDeleteContactSuccess?.(contactId)
      notify({
        id: NotificationKeys.ContactDeleted,
        variant: 'success',
        message: t(translationKeys.notificationSuccess, {
          name: selectedContact.name,
        }),
      })
      close()
    },
    onError: (apiError) => {
      const isContactUndeletable = apiError?.body?.errorCode === 'UNDELETABLE_RECORD'
      const errorMessageTranslationKey = isContactUndeletable
        ? translationKeys.notificationErrorUndeletable
        : translationKeys.notificationError
      const errorMessage = t(errorMessageTranslationKey, { name: selectedContact.name })
      notify({
        id: NotificationKeys.ContactDeleted,
        variant: 'error',
        message: errorMessage,
      })
      close()
    },
  })

  const handleConfirm = useCallback(() => {
    if (selectedContact.id) {
      deleteContact(selectedContact.id)
    }
  }, [deleteContact, selectedContact])

  const handleCancelClick = useCallback(() => {
    close()
  }, [close])

  return (
    <ModalConfirmation
      cancelLabel={t(translationKeys.cancel)}
      danger
      id={ModalId.DeleteContactConfirmationModal}
      message={t(translationKeys.message, {
        name: selectedContact.name,
      })}
      okLabel={t(translationKeys.confirmDelete)}
      okLoading={isProcessing}
      onCancel={handleCancelClick}
      onOk={handleConfirm}
      size="s"
      title={t(translationKeys.title)}
    />
  )
}
